import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Search, ExternalLink, Phone, CreditCard, Building2, AlertCircle, Globe } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { companyInfo } from '../mock';

const PaymentPage = () => {
  const { language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');

  // Texas Insurance Companies
  const texasInsuranceCompanies = [
    {
      name: 'Progressive',
      paymentUrl: 'https://account.apps.progressive.com/access/ez-payment/policy-info',
      verified: true,
      isDirectPayment: true
    },
    {
      name: 'GEICO',
      paymentUrl: 'https://ecams.geico.com/login',
      verified: true,
      isDirectPayment: false
    },
    {
      name: 'CoverCube',
      paymentUrl: 'https://customer.covercube.com/make-payment',
      verified: true,
      isDirectPayment: true
    },
    {
      name: 'Artesia Insurance Group',
      paymentUrl: 'https://auto.artesiainsgroup.com/insd/',
      verified: true,
      isDirectPayment: false
    },
    {
      name: 'Lemonade',
      paymentUrl: 'https://www.lemonade.com/login',
      verified: true,
      isDirectPayment: false
    },
    {
      name: 'Homeowners of America (HOAIC)',
      paymentUrl: 'https://portal.hoaic.com/login',
      verified: true,
      isDirectPayment: false
    },
    {
      name: 'First Acceptance',
      displayName: {
        en: 'First Acceptance — Make a Payment',
        es: 'First Acceptance — Hacer un pago'
      },
      paymentUrl: 'https://www.firstacceptance.com/make-a-payment/',
      verified: true,
      isDirectPayment: true
    },
    {
      name: 'Connect Insurance',
      paymentUrl: 'https://tx.connectinsurance.com/insd/',
      verified: true,
      isDirectPayment: false
    },
    {
      name: 'Advantage Auto MGA',
      paymentUrl: 'https://policyholder.advantageauto.com/policyholder/MakePayment',
      verified: true,
      isDirectPayment: true
    },
    {
      name: 'Alpine Rio',
      paymentUrl: 'https://www.alpinerio.com/Policy-Payments/Policy-Payment-Information',
      verified: true,
      isDirectPayment: false
    },
    {
      name: 'Falcon Insurance Group',
      paymentUrl: 'https://www.falconinsgroup.com/en/Customers/Payment',
      verified: true,
      isDirectPayment: true
    },
    {
      name: 'Aguila Dorada',
      paymentUrl: 'https://auto.ad-ga.com/insd/',
      verified: true,
      isDirectPayment: false
    },
    {
      name: 'MyClick Insurance',
      paymentUrl: null,
      verified: false,
      isDirectPayment: false
    },
    {
      name: 'Root Insurance',
      paymentUrl: 'https://www.joinroot.com',
      verified: true,
      isDirectPayment: false,
      note: {
        en: 'Mobile app required for payments',
        es: 'Requiere aplicación móvil para pagos'
      }
    },
    {
      name: 'Next Insurance',
      paymentUrl: 'https://portal.nextinsurance.com/login/authentication',
      verified: true,
      isDirectPayment: false
    },
    {
      name: 'Coterie Insurance',
      paymentUrl: 'https://mypolicycenter.coterieinsurance.com/',
      verified: true,
      isDirectPayment: false
    },
    {
      name: 'Kemper Auto',
      paymentUrl: 'https://www.kemper.com/my-policy/make-a-payment',
      verified: true,
      isDirectPayment: true
    },
    {
      name: 'Sterling MGA',
      paymentUrl: 'https://consumer.sterlingmga.com/pay-now/',
      verified: true,
      isDirectPayment: true
    },
    {
      name: 'Thimble',
      paymentUrl: 'https://app.thimble.com/login',
      verified: true,
      isDirectPayment: false
    }
  ];

  // Puerto Rico Insurance Companies
  const puertoRicoInsuranceCompanies = [
    {
      name: 'Cooperativa de Seguros Múltiples',
      paymentUrl: 'https://web.csm.coop/servicioenlinea/Pay/find.aspx',
      verified: true,
      isDirectPayment: true
    },
    {
      name: 'Universal Insurance Puerto Rico',
      paymentUrl: 'https://pay.universalpr.com/UPPublic/Home.aspx',
      verified: true,
      isDirectPayment: true
    },
    {
      name: 'MAPFRE Puerto Rico',
      paymentUrl: 'https://ww4.mapfrepr.com/Customer/Login',
      verified: true,
      isDirectPayment: false
    },
    {
      name: 'Multinational Insurance',
      paymentUrl: 'https://portalasegurados.multinationalpr.com/',
      verified: true,
      isDirectPayment: false
    },
    {
      name: 'Triple-S Propiedad',
      paymentUrl: 'https://propiedad.grupotriples.com/',
      verified: true,
      isDirectPayment: false
    },
    {
      name: 'Óptima Seguros',
      paymentUrl: 'https://www.optimapr.com/EPMS/',
      verified: true,
      isDirectPayment: false
    },
    {
      name: 'Chubb Puerto Rico',
      paymentUrl: 'https://portalpagos.chubb.com/front/quickpay/unauthenticated',
      verified: true,
      isDirectPayment: true
    },
    {
      name: 'Antilles Insurance',
      paymentUrl: 'https://antillesinsurance.com/seguros-personales/',
      verified: true,
      isDirectPayment: false
    },
    {
      name: 'One Alliance of Puerto Rico',
      paymentUrl: null,
      verified: false,
      isDirectPayment: false,
      phone: '787-622-3333'
    }
  ];

  const premiumFinanceCompanies = [
    {
      name: 'Spartan Financial Services',
      paymentUrl: 'https://spartan-financial.com/',
      verified: true,
      isDirectPayment: false,
      description: {
        en: 'Premium finance portal for account management and payments',
        es: 'Portal de financiamiento de primas para gestión de cuenta y pagos'
      }
    },
    {
      name: 'Imperial Premium Finance (IPFS)',
      paymentUrl: 'https://app.ipfs.com/Core/MakeAPayment',
      verified: true,
      isDirectPayment: true,
      description: {
        en: 'Direct payment portal with IPFS TotalPay',
        es: 'Portal directo de pagos con IPFS TotalPay'
      }
    },
    {
      name: 'Honor Capital',
      paymentUrl: 'https://www.honor.capital/',
      verified: true,
      isDirectPayment: false,
      description: {
        en: "America's 4th largest premium finance company",
        es: 'Cuarta financiera de primas más grande de América'
      }
    }
  ];

  const filteredTexasInsurance = texasInsuranceCompanies.filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPRInsurance = puertoRicoInsuranceCompanies.filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFinance = premiumFinanceCompanies.filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const content = {
    en: {
      title: 'Make a Payment',
      subtitle: 'Pay your insurance policy or premium finance bill online',
      searchPlaceholder: 'Search by company name...',
      texasTab: 'Texas',
      puertoRicoTab: 'Puerto Rico',
      insuranceSection: 'Insurance Companies',
      insuranceDesc: 'Select your insurance company to make a payment',
      financeSection: 'Premium Finance Companies',
      financeDesc: 'If you financed your premium, check your contract to confirm which finance company receives your payments',
      payButton: 'Pay Now',
      visitSite: 'Visit Site',
      contactAgency: 'Contact Agency',
      notListed: "Don't know your company?",
      callUs: `Call us at ${companyInfo.phone}`,
      helpText: 'We do not collect banking or card information on this website. All payments are processed securely through your insurance company or finance company portal.',
      verifiedNote: 'All payment links are verified and lead to official company portals',
      prIntro: 'Welcome Puerto Rico Clients',
      prDescription: 'Palerm Insurance Agency serves clients throughout Puerto Rico. Contact us to identify your insurance company and payment options.',
      prContact: 'Contact Information',
      prEmail: 'Email',
      prPhone: 'Phone',
      prRegulator: 'Puerto Rico Insurance Regulator',
      prHelp: 'Need help identifying your insurance company or payment portal? Contact us and we will assist you.'
    },
    es: {
      title: 'Realizar un Pago',
      subtitle: 'Pague su póliza de seguro o cuenta de financiamiento de prima en línea',
      searchPlaceholder: 'Buscar por nombre de compañía...',
      texasTab: 'Texas',
      puertoRicoTab: 'Puerto Rico',
      insuranceSection: 'Compañías de Seguros',
      insuranceDesc: 'Seleccione su compañía de seguros para realizar un pago',
      financeSection: 'Financieras de Seguros',
      financeDesc: 'Si financió su prima, revise su contrato para confirmar qué financiera recibe sus pagos',
      payButton: 'Pagar Ahora',
      visitSite: 'Visitar Sitio',
      contactAgency: 'Contactar Agencia',
      notListed: '¿No sabes cuál es tu compañía?',
      callUs: `Llámanos al ${companyInfo.phone}`,
      helpText: 'No recopilamos información bancaria ni de tarjetas en este sitio web. Todos los pagos se procesan de forma segura a través del portal de su compañía de seguros o financiera.',
      verifiedNote: 'Todos los enlaces de pago están verificados y conducen a portales oficiales de las compañías',
      prIntro: 'Bienvenidos Clientes de Puerto Rico',
      prDescription: 'Palerm Insurance Agency atiende clientes en todo Puerto Rico. Contáctenos para identificar su compañía de seguros y opciones de pago.',
      prContact: 'Información de Contacto',
      prEmail: 'Correo Electrónico',
      prPhone: 'Teléfono',
      prRegulator: 'Regulador de Seguros de Puerto Rico',
      prHelp: '¿Necesita ayuda para identificar su compañía de seguros o portal de pagos? Contáctenos y le asistiremos.'
    }
  };

  const text = content[language];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-700 to-blue-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/10 p-4 rounded-full mb-6">
              <CreditCard className="w-16 h-16" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {text.title}
            </h1>
            <p className="text-xl md:text-2xl text-blue-100">
              {text.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={text.searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-full focus:outline-none focus:ring-4 focus:ring-blue-300 focus:border-blue-500 text-lg"
                aria-label={text.searchPlaceholder}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Help Banner */}
      <section className="py-6 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-blue-100 border-l-4 border-blue-600 p-6 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-blue-700 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-bold text-blue-900 mb-2">
                    {text.notListed}
                  </p>
                  <p className="text-blue-800">
                    {text.callUs}
                  </p>
                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="inline-flex items-center gap-2 mt-3 text-blue-700 hover:text-blue-900 font-semibold"
                  >
                    <Phone className="w-4 h-4" />
                    {companyInfo.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Companies Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            
            {/* Tabs for Texas and Puerto Rico */}
            <Tabs defaultValue="texas" className="w-full">
              <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12">
                <TabsTrigger value="texas" className="text-lg font-semibold">
                  {text.texasTab}
                </TabsTrigger>
                <TabsTrigger value="puertorico" className="text-lg font-semibold">
                  {text.puertoRicoTab}
                </TabsTrigger>
              </TabsList>

              {/* Texas Tab Content */}
              <TabsContent value="texas" className="space-y-16">
                {/* Texas Insurance Companies */}
                <div>
                  <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                      {text.insuranceSection}
                    </h2>
                    <p className="text-lg text-gray-600">
                      {text.insuranceDesc}
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredTexasInsurance.map((company, index) => (
                      <Card key={index} className="hover:shadow-xl transition-all duration-300 border-2 border-gray-100 hover:border-blue-300">
                        <CardContent className="p-6 space-y-4">
                          <div className="flex items-center justify-between">
                            <h3 className="text-xl font-bold text-gray-900">
                              {company.displayName ? company.displayName[language] : company.name}
                            </h3>
                            {company.verified && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                {language === 'en' ? 'Verified' : 'Verificado'}
                              </span>
                            )}
                          </div>

                          {company.note && (
                            <p className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded">
                              {company.note[language]}
                            </p>
                          )}

                          {company.paymentUrl ? (
                            <a
                              href={company.paymentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`block w-full ${
                                company.isDirectPayment 
                                  ? 'bg-red-600 hover:bg-red-700' 
                                  : 'bg-blue-600 hover:bg-blue-700'
                              } text-white px-6 py-3 rounded-full text-center font-semibold transition-all duration-300 focus:outline-none focus:ring-4 ${
                                company.isDirectPayment 
                                  ? 'focus:ring-red-300' 
                                  : 'focus:ring-blue-300'
                              }`}
                            >
                              <span className="flex items-center justify-center gap-2">
                                {company.isDirectPayment ? text.payButton : text.visitSite}
                                <ExternalLink className="w-4 h-4" />
                              </span>
                            </a>
                          ) : (
                            <a
                              href={`tel:${companyInfo.phone}`}
                              className="block w-full bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-full text-center font-semibold transition-all duration-300"
                            >
                              <span className="flex items-center justify-center gap-2">
                                <Phone className="w-4 h-4" />
                                {text.contactAgency}
                              </span>
                            </a>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {filteredTexasInsurance.length === 0 && searchTerm && (
                    <div className="text-center py-12">
                      <p className="text-gray-600 text-lg">
                        {language === 'en' 
                          ? 'No insurance companies found matching your search.' 
                          : 'No se encontraron compañías de seguros que coincidan con tu búsqueda.'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Texas Premium Finance Section */}
                <div className="bg-gray-50 py-16 -mx-4 px-4">
                  <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-3">
                        <Building2 className="w-10 h-10 text-blue-700" />
                        {text.financeSection}
                      </h2>
                      <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                        {text.financeDesc}
                      </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                      {filteredFinance.map((company, index) => (
                        <Card key={index} className="hover:shadow-xl transition-all duration-300 border-2 border-gray-100 hover:border-blue-300">
                          <CardContent className="p-6 space-y-4">
                            <div className="flex items-center justify-between">
                              <h3 className="text-xl font-bold text-gray-900">{company.name}</h3>
                              {company.verified && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                  {language === 'en' ? 'Verified' : 'Verificado'}
                                </span>
                              )}
                            </div>

                            {company.description && (
                              <p className="text-sm text-gray-600">
                                {company.description[language]}
                              </p>
                            )}

                            <a
                              href={company.paymentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`block w-full ${
                                company.isDirectPayment 
                                  ? 'bg-red-600 hover:bg-red-700 focus:ring-red-300' 
                                  : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-300'
                              } text-white px-6 py-3 rounded-full text-center font-semibold transition-all duration-300 focus:outline-none focus:ring-4`}
                            >
                              <span className="flex items-center justify-center gap-2">
                                {company.isDirectPayment ? text.payButton : text.visitSite}
                                <ExternalLink className="w-4 h-4" />
                              </span>
                            </a>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {filteredFinance.length === 0 && searchTerm && (
                      <div className="text-center py-12">
                        <p className="text-gray-600 text-lg">
                          {language === 'en' 
                            ? 'No finance companies found matching your search.' 
                            : 'No se encontraron financieras que coincidan con tu búsqueda.'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Puerto Rico Tab Content */}
              <TabsContent value="puertorico" className="space-y-16">
                {/* Puerto Rico Intro Section */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 md:p-12 border-2 border-blue-200">
                  <div className="flex items-center gap-4 mb-6">
                    <Globe className="w-12 h-12 text-blue-700" />
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                      {text.prIntro}
                    </h2>
                  </div>
                  <p className="text-lg text-gray-700 mb-8">
                    {text.prDescription}
                  </p>
                  
                  {/* Contact Info */}
                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-white rounded-lg p-6 shadow-md">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">{text.prContact}</h3>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <Phone className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="text-sm text-gray-600">{text.prPhone}</p>
                            <a href={`tel:${companyInfo.phone}`} className="text-blue-700 font-semibold hover:underline">
                              {companyInfo.phone}
                            </a>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <ExternalLink className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="text-sm text-gray-600">{text.prEmail}</p>
                            <a href="mailto:info@palerminsurance.com" className="text-blue-700 font-semibold hover:underline">
                              info@palerminsurance.com
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg p-6 shadow-md">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">{text.prRegulator}</h3>
                      <a
                        href="https://www.ocs.pr.gov"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-blue-700 font-semibold hover:underline"
                      >
                        OCS Puerto Rico
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <p className="text-sm text-gray-600 mt-2">
                        {language === 'en' 
                          ? 'Office of the Commissioner of Insurance' 
                          : 'Oficina del Comisionado de Seguros'}
                      </p>
                    </div>
                  </div>

                  <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg">
                    <p className="text-gray-700">
                      <strong className="text-amber-900">{language === 'en' ? 'Need Help?' : '¿Necesita Ayuda?'}</strong>{' '}
                      {text.prHelp}
                    </p>
                  </div>
                </div>

                {/* Puerto Rico Insurance Companies */}
                <div>
                  <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                      {text.insuranceSection}
                    </h2>
                    <p className="text-lg text-gray-600">
                      {text.insuranceDesc}
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                    {filteredPRInsurance.map((company, index) => (
                      <Card key={index} className="hover:shadow-xl transition-all duration-300 border-2 border-gray-100 hover:border-blue-300">
                        <CardContent className="p-6 space-y-4">
                          <div className="flex items-center justify-between">
                            <h3 className="text-xl font-bold text-gray-900">{company.name}</h3>
                            {company.verified && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                {language === 'en' ? 'Verified' : 'Verificado'}
                              </span>
                            )}
                          </div>

                          {company.paymentUrl ? (
                            <a
                              href={company.paymentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`block w-full ${
                                company.isDirectPayment 
                                  ? 'bg-red-600 hover:bg-red-700' 
                                  : 'bg-blue-600 hover:bg-blue-700'
                              } text-white px-6 py-3 rounded-full text-center font-semibold transition-all duration-300 focus:outline-none focus:ring-4 ${
                                company.isDirectPayment 
                                  ? 'focus:ring-red-300' 
                                  : 'focus:ring-blue-300'
                              }`}
                            >
                              <span className="flex items-center justify-center gap-2">
                                {company.isDirectPayment ? text.payButton : text.visitSite}
                                <ExternalLink className="w-4 h-4" />
                              </span>
                            </a>
                          ) : (
                            <a
                              href={`tel:${company.phone || companyInfo.phone}`}
                              className="block w-full bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-full text-center font-semibold transition-all duration-300"
                            >
                              <span className="flex items-center justify-center gap-2">
                                <Phone className="w-4 h-4" />
                                {company.phone ? `${language === 'en' ? 'Call' : 'Llamar'} ${company.phone}` : text.contactAgency}
                              </span>
                            </a>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {filteredPRInsurance.length === 0 && searchTerm && (
                    <div className="text-center py-12">
                      <p className="text-gray-600 text-lg">
                        {language === 'en' 
                          ? 'No insurance companies found matching your search.' 
                          : 'No se encontraron compañías de seguros que coincidan con tu búsqueda.'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Puerto Rico Premium Finance Section */}
                <div className="bg-gray-50 py-16 -mx-4 px-4">
                  <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-3">
                        <Building2 className="w-10 h-10 text-blue-700" />
                        {text.financeSection}
                      </h2>
                      <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                        {text.financeDesc}
                      </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                      {filteredFinance.map((company, index) => (
                        <Card key={index} className="hover:shadow-xl transition-all duration-300 border-2 border-gray-100 hover:border-blue-300">
                          <CardContent className="p-6 space-y-4">
                            <div className="flex items-center justify-between">
                              <h3 className="text-xl font-bold text-gray-900">{company.name}</h3>
                              {company.verified && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                  {language === 'en' ? 'Verified' : 'Verificado'}
                                </span>
                              )}
                            </div>

                            {company.description && (
                              <p className="text-sm text-gray-600">
                                {company.description[language]}
                              </p>
                            )}

                            <a
                              href={company.paymentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`block w-full ${
                                company.isDirectPayment 
                                  ? 'bg-red-600 hover:bg-red-700 focus:ring-red-300' 
                                  : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-300'
                              } text-white px-6 py-3 rounded-full text-center font-semibold transition-all duration-300 focus:outline-none focus:ring-4`}
                            >
                              <span className="flex items-center justify-center gap-2">
                                {company.isDirectPayment ? text.payButton : text.visitSite}
                                <ExternalLink className="w-4 h-4" />
                              </span>
                            </a>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>

          </div>
        </div>
      </section>

      {/* Security Notice */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-green-50 border-l-4 border-green-600 p-6 rounded-lg">
              <p className="text-sm text-gray-700 flex items-start gap-3">
                <span className="text-green-600 text-xl flex-shrink-0">🔒</span>
                <span>
                  <strong>{language === 'en' ? 'Security Notice:' : 'Aviso de Seguridad:'}</strong>{' '}
                  {text.helpText}
                </span>
              </p>
            </div>
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                <span className="inline-flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  {text.verifiedNote}
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PaymentPage;
